"use client";

import { createContext, useContext, useEffect, useRef, useState, ReactNode, useCallback } from "react";
import { createLogger } from "@/lib/logger";
import { useVisibilityRef } from "@/hooks/useVisibilityRef";
import { cdn } from "@/lib/cdn";

const logger = createLogger("NewsNotifications");

// =============================================================================
// TYPES - Extensible for future sources (Twitter, Discord, etc.)
// =============================================================================

type NewsSource = "crypto-rss" | "twitter" | "discord" | "telegram";

interface NewsItem {
  id: string;
  title: string;
  link: string;
  source: NewsSource;
  subSource?: string; // e.g., "beincrypto", "cointelegraph", "@elonmusk"
  category?: string; // e.g., "BTC", "ETH", "SOL", "XRP"
  timestamp: Date;
  image?: string; // thumbnail image URL
}

interface NewsNotificationsContextType {
  // State
  isEnabled: boolean;
  setIsEnabled: (enabled: boolean) => void;

  // News items (for potential UI display)
  latestNews: NewsItem[];

  // Manual refresh
  refreshNews: () => Promise<void>;
}

const NewsNotificationsContext = createContext<NewsNotificationsContextType | null>(null);

// =============================================================================
// CONFIGURATION
// =============================================================================

const CONFIG = {
  // How often to check for new news (in ms)
  POLL_INTERVAL: 30000, // 30 seconds

  // Max toasts to show at once per refresh
  MAX_TOASTS_PER_REFRESH: 3,

  // Toast duration in ms
  TOAST_DURATION_DESKTOP: 10000, // 10 seconds for desktop
  TOAST_DURATION_MOBILE: 4000,   // 4 seconds for mobile - less intrusive

  // Swipe threshold to dismiss (in pixels)
  SWIPE_THRESHOLD: 80,

  // How many recent items to keep in memory
  MAX_ITEMS_IN_MEMORY: 50,
};

// Emoji mapping for crypto categories
const CRYPTO_EMOJIS: Record<string, string> = {
  BTC: "₿",
  ETH: "Ξ",
  SOL: "◎",
  XRP: "✕",
};

// Emoji mapping for sources (empty string means no emoji, just text)
const SOURCE_EMOJIS: Record<string, string> = {
  "crypto-rss": "📰",
  twitter: "", // No emoji, title will just show "X @frenflow"
  discord: "💬",
  telegram: "✈️",
};

// Source labels for display
const SOURCE_LABELS: Record<string, string> = {
  "crypto-rss": "Crypto News",
  twitter: "Twitter",
  discord: "Discord",
  telegram: "Telegram",
};

// Per-outlet config for the toast footer. `logoIsWordmark: true` means the
// asset already contains the brand name (icon + text), so we render the
// image alone at a larger size instead of icon + separate label. For outlets
// we don't have a local wordmark for, fall back to Google's S2 favicon
// service + the pretty label, with an onerror hook that drops the image if
// it ever fails to load.
const RSS_OUTLET_CONFIG: Record<string, { label: string; logoUrl: string; logoIsWordmark?: boolean }> = {
  beincrypto: {
    label: "BeInCrypto",
    logoUrl: "https://www.google.com/s2/favicons?domain=beincrypto.com&sz=64",
  },
  cointelegraph: {
    label: "CoinTelegraph",
    logoUrl: "https://www.google.com/s2/favicons?domain=cointelegraph.com&sz=64",
  },
  dextools: {
    label: "DEXTools",
    logoUrl: "/logos/dextools-logo.png",
    logoIsWordmark: true,
  },
};

// =============================================================================
// NEWS TOAST COMPONENT (Frenflow style)
// =============================================================================

function NewsToastItem({ item, onDismiss }: { item: NewsItem; onDismiss: () => void }) {
  const [isVisible, setIsVisible] = useState(false);
  const [swipeX, setSwipeX] = useState(0);
  const [isSwiping, setIsSwiping] = useState(false);
  const touchStartX = useRef(0);
  const isMobile = useRef(typeof window !== 'undefined' && (window.matchMedia('(max-width: 768px)').matches || 'ontouchstart' in window));

  // Update mobile detection on mount
  useEffect(() => {
    isMobile.current = window.matchMedia('(max-width: 768px)').matches ||
      'ontouchstart' in window;
  }, []);

  useEffect(() => {
    // Animate in
    setTimeout(() => setIsVisible(true), 10);

    // Auto dismiss after duration (shorter on mobile)
    const duration = isMobile.current
      ? CONFIG.TOAST_DURATION_MOBILE
      : CONFIG.TOAST_DURATION_DESKTOP;

    const timer = setTimeout(() => {
      setIsVisible(false);
      setTimeout(onDismiss, 300); // Wait for animation
    }, duration);

    return () => clearTimeout(timer);
  }, [onDismiss]);

  const handleClick = () => {
    // Don't open link if user was swiping
    if (isSwiping) return;

    // Open news in popup window
    const width = 900;
    const height = 700;
    const left = window.screenX + (window.outerWidth - width) / 2;
    const top = window.screenY + (window.outerHeight - height) / 2;
    window.open(
      item.link,
      "frenflow_news",
      `width=${width},height=${height},left=${left},top=${top},popup=yes,noopener,noreferrer`
    );
  };

  const handleDismiss = (e: React.MouseEvent) => {
    e.stopPropagation();
    setIsVisible(false);
    setTimeout(onDismiss, 300);
  };

  // Touch handlers for swipe to dismiss
  const handleTouchStart = (e: React.TouchEvent) => {
    touchStartX.current = e.touches[0].clientX;
    setIsSwiping(false);
  };

  const handleTouchMove = (e: React.TouchEvent) => {
    const currentX = e.touches[0].clientX;
    const diff = currentX - touchStartX.current;

    // Only allow swiping right (positive diff)
    if (diff > 0) {
      setSwipeX(diff);
      setIsSwiping(true);
    }
  };

  const handleTouchEnd = () => {
    if (swipeX > CONFIG.SWIPE_THRESHOLD) {
      // Swipe threshold reached - dismiss
      setSwipeX(200); // Animate out
      setTimeout(() => {
        setIsVisible(false);
        onDismiss();
      }, 200);
    } else {
      // Snap back
      setSwipeX(0);
      // Reset swiping state after a short delay to prevent accidental clicks
      setTimeout(() => setIsSwiping(false), 100);
    }
  };

  // Determine emoji
  const isTwitter = item.source === "twitter";
  const emoji = item.category ? (CRYPTO_EMOJIS[item.category] || "") : "";

  // Calculate opacity based on swipe distance
  const swipeOpacity = Math.max(0, 1 - swipeX / 150);

  return (
    <div
      onClick={handleClick}
      onTouchStart={handleTouchStart}
      onTouchMove={handleTouchMove}
      onTouchEnd={handleTouchEnd}
      style={{
        transform: `translateX(${swipeX > 0 ? swipeX : (isVisible ? 0 : 16)}px)`,
        opacity: swipeX > 0 ? swipeOpacity : (isVisible ? 1 : 0),
        transition: isSwiping ? 'none' : 'all 0.3s ease-out',
      }}
      className={`bg-[#111] border border-[#222] rounded-[5px] p-3 w-[320px] max-w-[calc(100vw-2rem)] shadow-xl cursor-pointer hover:border-white/20`}
    >
      <div className="flex gap-3">
        {/* Thumbnail image */}
        {item.image && (
          <div className="w-14 h-14 rounded-[5px] overflow-hidden flex-shrink-0 bg-[#1A1A1A]">
            <img
              src={item.image}
              alt=""
              className="w-full h-full object-cover"
              onError={(e) => {
                (e.target as HTMLImageElement).style.display = 'none';
              }}
            />
          </div>
        )}

        {/* Content */}
        <div className="flex-1 min-w-0">
          {/* Title with inline tag */}
          <div className="flex items-start gap-2">
            <p className="text-[13px] text-white font-satoshi font-medium line-clamp-2 leading-snug flex-1">
              {item.title}
            </p>
            <button
              onClick={handleDismiss}
              className="text-gray-600 hover:text-white transition-colors text-xs mt-0.5 flex-shrink-0"
            >
              ✕
            </button>
          </div>

          {/* Footer: source + category */}
          <div className="flex items-center gap-2 mt-1.5">
            {isTwitter ? (
              <div className="flex items-center gap-1">
                <img src={cdn("/icons/x-logo.svg")} alt="X" className="w-2.5 h-2.5 invert opacity-50" />
                <span className="text-[10px] text-gray-500">{item.subSource || "@frenflow"}</span>
              </div>
            ) : (() => {
              const outlet = item.subSource ? RSS_OUTLET_CONFIG[item.subSource] : null;
              const label = outlet?.label || item.subSource || "News";
              // Wordmark assets already include the outlet name — render the
              // image alone at a taller size. Favicon assets are paired with
              // the text label next to them.
              if (outlet?.logoIsWordmark) {
                return (
                  <img
                    src={outlet.logoUrl}
                    alt={outlet.label}
                    className="h-3 w-auto opacity-70"
                    onError={(e) => {
                      // Fallback to the text label instead of hiding everything
                      // so the outlet stays identifiable if the asset ever 404s.
                      const img = e.target as HTMLImageElement;
                      const parent = img.parentElement;
                      if (parent) {
                        parent.innerHTML = `<span class="text-[10px] text-gray-500">${outlet.label}</span>`;
                      }
                    }}
                  />
                );
              }
              return (
                <div className="flex items-center gap-1">
                  {outlet?.logoUrl && (
                    <img
                      src={outlet.logoUrl}
                      alt=""
                      className="w-3 h-3 rounded-sm object-contain"
                      onError={(e) => {
                        (e.target as HTMLImageElement).style.display = "none";
                      }}
                    />
                  )}
                  <span className="text-[10px] text-gray-500">{label}</span>
                </div>
              );
            })()}
            {item.category && (
              <>
                <span className="text-gray-600">·</span>
                <span className="text-[10px] font-medium text-white/70">
                  {emoji} {item.category}
                </span>
              </>
            )}
            <span className="text-gray-600">·</span>
            <span className="text-[10px] text-gray-500">Tap to read</span>
          </div>
        </div>
      </div>
    </div>
  );
}

// =============================================================================
// NEWS TOAST CONTAINER (Fixed top-right, stacks vertically)
// =============================================================================

function NewsToastContainer({ toasts, onDismiss }: { toasts: NewsItem[]; onDismiss: (id: string) => void }) {
  // Hide in Telegram MiniApp
  const isMiniApp = typeof window !== 'undefined' && (
    window.location.pathname.startsWith('/telegram/miniapp') ||
    !!(window as any).Telegram?.WebApp?.initData
  );

  if (isMiniApp || toasts.length === 0) return null;

  return (
    <div className="fixed top-[56px] right-4 z-[99999] flex flex-col gap-3">
      {toasts.map((item) => (
        <NewsToastItem
          key={item.id}
          item={item}
          onDismiss={() => onDismiss(item.id)}
        />
      ))}
    </div>
  );
}

// =============================================================================
// PROVIDER
// =============================================================================

interface NewsNotificationsProviderProps {
  children: ReactNode;
}

const SEEN_NEWS_STORAGE_KEY = "frenflow-seen-news";
const NEWS_ENABLED_STORAGE_KEY = "frenflow-news-notifications-enabled";
const SEEN_NEWS_MAX_AGE = 24 * 60 * 60 * 1000; // 24 hours

export function NewsNotificationsProvider({ children }: NewsNotificationsProviderProps) {
  // Default to false, load from localStorage
  const [isEnabled, setIsEnabledState] = useState(false);
  const visRef = useVisibilityRef();

  // Load enabled state from localStorage on mount
  useEffect(() => {
    try {
      const stored = localStorage.getItem(NEWS_ENABLED_STORAGE_KEY);
      if (stored !== null) {
        setIsEnabledState(stored === "true");
      }
    } catch (e) {
      logger.error("Error loading news enabled state", { error: e instanceof Error ? e.message : String(e) });
    }
  }, []);

  // Wrapper to persist to localStorage
  const setIsEnabled = useCallback((enabled: boolean) => {
    setIsEnabledState(enabled);
    try {
      localStorage.setItem(NEWS_ENABLED_STORAGE_KEY, String(enabled));
    } catch (e) {
      logger.error("Error saving news enabled state", { error: e instanceof Error ? e.message : String(e) });
    }
  }, []);
  const [latestNews, setLatestNews] = useState<NewsItem[]>([]);
  const [visibleToasts, setVisibleToasts] = useState<NewsItem[]>([]);

  // Track seen news to detect new items (persisted in localStorage)
  const seenNewsIdsRef = useRef<Set<string>>(new Set());
  const intervalRef = useRef<NodeJS.Timeout | null>(null);

  // Load seen news from localStorage on mount
  useEffect(() => {
    try {
      const stored = localStorage.getItem(SEEN_NEWS_STORAGE_KEY);
      if (stored) {
        const parsed = JSON.parse(stored) as { ids: string[]; timestamp: number };
        // Only use if less than 24 hours old
        if (Date.now() - parsed.timestamp < SEEN_NEWS_MAX_AGE) {
          parsed.ids.forEach(id => seenNewsIdsRef.current.add(id));
        }
      }
    } catch (e) {
      logger.error("Error loading seen news", { error: e instanceof Error ? e.message : String(e) });
    }
  }, []);

  // Dismiss a toast by ID
  const dismissToast = useCallback((id: string) => {
    setVisibleToasts(prev => prev.filter(t => t.id !== id));
  }, []);

  // =========================================================================
  // FETCH CRYPTO RSS NEWS
  // =========================================================================
  const fetchCryptoNews = async (): Promise<NewsItem[]> => {
    try {
      const response = await fetch("/api/crypto-news");
      if (!response.ok) return [];

      const data = await response.json();
      const news = data.news || [];

      return news.map((item: any) => ({
        id: item.link, // Use link as unique ID
        title: item.title,
        link: item.link,
        source: "crypto-rss" as NewsSource,
        subSource: item.source, // beincrypto, cointelegraph
        category: item.crypto, // BTC, ETH, SOL, XRP
        timestamp: new Date(item.pubDate),
        image: item.image, // thumbnail from RSS
      }));
    } catch (error) {
      logger.error("Error fetching crypto news", { error: error instanceof Error ? error.message : String(error) });
      return [];
    }
  };

  // =========================================================================
  // FETCH TWITTER NEWS (@frenflow)
  // =========================================================================
  const fetchTwitterNews = async (): Promise<NewsItem[]> => {
    try {
      const response = await fetch("/api/twitter-feed");
      if (!response.ok) return [];

      const data = await response.json();
      const tweets = data.tweets || [];

      return tweets.map((tweet: any) => ({
        id: `twitter-${tweet.id}`,
        title: tweet.text,
        link: `https://twitter.com/${tweet.author_username}/status/${tweet.id}`,
        source: "twitter" as NewsSource,
        subSource: `@${tweet.author_username}`,
        category: undefined, // Could detect crypto mentions later
        timestamp: new Date(tweet.created_at),
        image: tweet.media?.[0]?.preview_url || tweet.media?.[0]?.url || tweet.author_profile_image,
      }));
    } catch (error) {
      logger.error("Error fetching Twitter", { error: error instanceof Error ? error.message : String(error) });
      return [];
    }
  };

  // =========================================================================
  // MAIN REFRESH FUNCTION
  // =========================================================================
  const refreshNews = async () => {
    if (!isEnabled) return;

    // Fetch from all sources in parallel
    const [cryptoNews, twitterNews] = await Promise.all([
      fetchCryptoNews(),
      fetchTwitterNews(),
    ]);

    // Combine all news
    const allNews = [...cryptoNews, ...twitterNews];

    // Detect new items (not seen before)
    const newItems = allNews.filter(item => !seenNewsIdsRef.current.has(item.id));

    // Show toasts for new items (including first load)
    if (newItems.length > 0) {
      // Prioritize Twitter tweets from last 24 hours
      const now = new Date();
      const oneDayAgo = new Date(now.getTime() - 24 * 60 * 60 * 1000);

      const recentTwitter = newItems.filter(
        item => item.source === "twitter" && item.timestamp > oneDayAgo
      );
      const otherNews = newItems.filter(
        item => !(item.source === "twitter" && item.timestamp > oneDayAgo)
      );

      // Twitter first, then other news, limited to max toasts
      const prioritizedItems = [...recentTwitter, ...otherNews].slice(0, CONFIG.MAX_TOASTS_PER_REFRESH);

      prioritizedItems.forEach((item, index) => {
        // Stagger toasts slightly for better UX
        setTimeout(() => {
          showNewsToast(item);
        }, index * 300);
      });
    }

    // Update seen IDs and persist to localStorage
    allNews.forEach(item => seenNewsIdsRef.current.add(item.id));
    try {
      localStorage.setItem(SEEN_NEWS_STORAGE_KEY, JSON.stringify({
        ids: Array.from(seenNewsIdsRef.current).slice(-100), // Keep last 100 IDs
        timestamp: Date.now(),
      }));
    } catch (e) {
      logger.error("Error saving seen news", { error: e instanceof Error ? e.message : String(e) });
    }

    // Update state with latest news (sorted by timestamp)
    setLatestNews(
      allNews
        .sort((a, b) => b.timestamp.getTime() - a.timestamp.getTime())
        .slice(0, CONFIG.MAX_ITEMS_IN_MEMORY)
    );
  };

  // =========================================================================
  // SHOW TOAST NOTIFICATION (adds to visible toasts)
  // =========================================================================
  const showNewsToast = useCallback((item: NewsItem) => {
    setVisibleToasts(prev => {
      // Don't add if already visible
      if (prev.some(t => t.id === item.id)) return prev;
      // Add new toast at the end (bottom of stack)
      return [...prev, item];
    });
  }, []);

  // =========================================================================
  // DEV HELPER — window.__ffTriggerNewsToast(overrides?)
  //
  // Injects a synthetic toast without hitting the RSS API, so QA can verify
  // styling for any outlet even when the live feed has no matching items.
  // Example from the browser console:
  //   __ffTriggerNewsToast()                                    // DexTools default
  //   __ffTriggerNewsToast({ subSource: 'cointelegraph' })      // override outlet
  //   __ffTriggerNewsToast({ category: 'ETH', title: 'Hello' })
  // =========================================================================
  useEffect(() => {
    if (typeof window === 'undefined') return;
    (window as any).__ffTriggerNewsToast = (overrides: Partial<NewsItem> = {}) => {
      const fake: NewsItem = {
        id: `debug-${Date.now()}-${Math.random().toString(36).slice(2, 7)}`,
        title: 'XRP hits new ATH as institutional inflows accelerate (synthetic toast)',
        link: 'https://www.dextools.io/news',
        source: 'crypto-rss',
        subSource: 'dextools',
        category: 'XRP',
        timestamp: new Date(),
        ...overrides,
      };
      showNewsToast(fake);
      return fake;
    };
    return () => { delete (window as any).__ffTriggerNewsToast; };
  }, [showNewsToast]);

  // =========================================================================
  // LIFECYCLE - Start/stop polling
  // =========================================================================
  useEffect(() => {
    if (!isEnabled) {
      // Clear interval when disabled
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
        intervalRef.current = null;
      }
      return;
    }

    // Initial fetch
    refreshNews();

    // Set up polling interval
    intervalRef.current = setInterval(() => { if (visRef.current) refreshNews(); }, CONFIG.POLL_INTERVAL);

    // Cleanup on unmount or when disabled
    return () => {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
        intervalRef.current = null;
      }
    };
  }, [isEnabled]);

  // =========================================================================
  // CONTEXT VALUE
  // =========================================================================
  const value: NewsNotificationsContextType = {
    isEnabled,
    setIsEnabled,
    latestNews,
    refreshNews,
  };

  return (
    <NewsNotificationsContext.Provider value={value}>
      {children}
      <NewsToastContainer toasts={visibleToasts} onDismiss={dismissToast} />
    </NewsNotificationsContext.Provider>
  );
}

// =============================================================================
// HOOK
// =============================================================================

export function useNewsNotifications() {
  const context = useContext(NewsNotificationsContext);
  if (!context) {
    throw new Error("useNewsNotifications must be used within NewsNotificationsProvider");
  }
  return context;
}
